import React, { useMemo } from 'react';
import { Box, Typography } from '@mui/material';
import { useAppSelector } from '@common-reducers/hooks/store.hook';
import { selectMessageById } from '@/components/team-inbox/redux/TeamInboxSelectors';
import { getWaContactBySerializedId } from '@common-reducers/wa-contact-entity-lookup';
import { calculatedWaContactName } from '@common-reducers/WhatsAppSelectors';
import ContactAvatar from '@/extension/context/content/components/bulk-dashboard/contact-table/contact-avatar';
import TeamInboxMessage from '@/components/team-inbox/team-inbox-message';
import SourceBadge from '@/components/team-inbox/source-badge';

const AVATAR_SIZE = 34;

/**
 * One all-chats verification row: [chat avatar + chat label] on the left, the
 * reused team-inbox message bubble on the right, a source badge (WA/VAULT/DB)
 * on top. Reads the message from the shared team-inbox cache by id (populated
 * by `useBackupSource`). Purpose-built for the /view-backup debug page — unlike
 * `WaMessagesDisplayRow` it always surfaces which chat a message belongs to,
 * since the list mixes every chat together.
 */
const ViewBackupMessageRow = React.memo(({ itemId }: { itemId: string }) => {
    const message = useAppSelector((state) => selectMessageById(state, itemId));
    const chatId: string = message?.chatId ?? '';
    const contactName = useAppSelector((state) => {
        if (!chatId) return '';
        const contact = getWaContactBySerializedId(state.DBWaContactsCacheReducer?.entities, chatId);
        return contact ? calculatedWaContactName(contact) : '';
    });

    const label = useMemo(() => contactName || chatId || 'unknown', [contactName, chatId]);

    if (!message) return <div style={{ height: 1 }} />;

    const fromMe = !!message.fromMe;

    return (
        <Box
            sx={{
                display: 'flex',
                alignItems: 'flex-start',
                gap: '10px',
                padding: '6px 16px',
                borderBottom: '1px solid var(--divider-color, rgba(0,0,0,0.06))',
            }}
        >
            {/* Chat identity gutter */}
            <Box sx={{ flexShrink: 0, width: 150, display: 'flex', alignItems: 'center', gap: '8px' }}>
                <ContactAvatar contactId={chatId} size={AVATAR_SIZE} textSize={13} />
                <Box sx={{ minWidth: 0 }}>
                    <Typography
                        sx={{
                            fontSize: '12.5px',
                            fontWeight: 600,
                            whiteSpace: 'nowrap',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            color: 'var(--primary-text-color, #111b21)',
                        }}
                        title={chatId}
                    >
                        {label}
                    </Typography>
                    <Typography sx={{ fontSize: '10.5px', color: 'var(--secondary-text-color, #667781)' }}>
                        {fromMe ? 'sent' : 'received'}
                    </Typography>
                </Box>
            </Box>

            {/* Reused bubble + tier badge */}
            <Box sx={{ position: 'relative', minWidth: 0, flex: 1 }}>
                <Box sx={{ display: 'inline-block', maxWidth: '100%' }}>
                    <TeamInboxMessage message={message} isFirstOfCluster />
                </Box>
                <Box sx={{ position: 'absolute', top: '-6px', left: '-6px' }}>
                    <SourceBadge source={message.source} size="small" />
                </Box>
            </Box>
        </Box>
    );
});

export default ViewBackupMessageRow;
